/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { ReactNode, useEffect, useState } from 'react';
import {
  Link,
  RouteComponentProps,
  navigate,
  useLocation,
} from '@reach/router';
import { Grid, Divider, Typography } from '@material-ui/core';
import colors from '../../../../../themes/main-colors';
import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import routes from '../../../../../routes';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import ReactMarkdown from 'react-markdown/with-html';
import Buttons from '../../../../buttons';
import useERApplicationApi from '../../../../../hooks/pge-plus/useERApplicationApi';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import {
  ApplicationErSectionType,
  ErEnrollmentPreference,
  SectionErRebateConfirm,
} from '../../../../../__generated__/pgeplus-types';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import useUtil from '../../../../need-more-time-to-pay/useUtil';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import { rebateConfirmERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import { ChargerType } from '../rebateOnlyForm.types';
import {
  handleInstructionsSectionErrors,
  isLegacyTeslaResumeCustomer,
} from '../rebateOnlyForm.utils';

type TChargerModelState = {
  chargerModel: string;
};

const ChargerInstructions: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const [instructionSteps, setInstructionSteps] = useState<
    { step: number; text: string }[]
  >([]);

  const location = useLocation();
  const { chargerModel } = (location.state || {}) as TChargerModelState;
  const {
    formState: { rebateConfirm, application, rebateInfo },
    getSectionData,
    updateFormState,
  } = useRebateOnlyFormState();

  const { content, richText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_CHARGER_INSTRUCTIONS,
  );
  const chargePointInstructions = [
    {
      step: 1,
      text: content.get('CHARGEPOINT_CHARGER_INSTRUCTIONS_1') || '',
    },
    {
      step: 2,
      text: content.get('CHARGEPOINT_CHARGER_INSTRUCTIONS_2') || '',
    },
    {
      step: 3,
      text: content.get('CHARGEPOINT_CHARGER_INSTRUCTIONS_3') || '',
    },
  ];

  const floInstructions = [
    {
      step: 1,
      text: content.get('FLO_CHARGER_INSTRUCTIONS_1') || '',
    },
    {
      step: 2,
      text: content.get('FLO_CHARGER_INSTRUCTIONS_2') || '',
    },
    {
      step: 3,
      text: content.get('FLO_CHARGER_INSTRUCTIONS_3') || '',
    },
  ];

  const juiceBoxInstructions = [
    {
      step: 1,
      text: content.get('JUICEBOX_CHARGER_INSTRUCTIONS_1') || '',
    },
    {
      step: 2,
      text: content.get('JUICEBOX_CHARGER_INSTRUCTIONS_2') || '',
    },
  ];

  const evPulseInstructions = [
    {
      step: 1,
      text: content.get('EVPULSE_CONNECTION_INSTRUCTIONS_1') || '',
    },
    {
      step: 2,
      text: content.get('EVPULSE_CONNECTION_INSTRUCTIONS_2') || '',
    },
  ];

  const wallBoxInstructions = [
    {
      step: 1,
      text: content.get('WALLBOX_CHARGER_INSTRUCTIONS_1') || '',
    },
    {
      step: 2,
      text: content.get('WALLBOX_CHARGER_INSTRUCTIONS_2') || '',
    },
    {
      step: 3,
      text: content.get('WALLBOX_CHARGER_INSTRUCTIONS_3') || '',
    },
  ];

  const emporiaInstructions = [
    {
      step: 1,
      text: content.get('EMPORIA_CHARGER_INSTRUCTIONS_1') || '',
    },
    {
      step: 2,
      text: content.get('EMPORIA_CHARGER_INSTRUCTIONS_2') || '',
    },
    {
      step: 3,
      text: content.get('EMPORIA_CHARGER_INSTRUCTIONS_3') || '',
    },
  ];

  const evoChargeInstructions = [
    {
      step: 1,
      text: content.get('EVOCHARGE_CHARGER_INSTRUCTIONS_1') || '',
    },
    {
      step: 2,
      text: content.get('EVOCHARGE_CHARGER_INSTRUCTIONS_2') || '',
    },
    {
      step: 3,
      text: content.get('EVOCHARGE_CHARGER_INSTRUCTIONS_3') || '',
    },
  ];

  useEffect(() => {
    if (content.size === 0) {
      return;
    }
    if (chargerModel) {
      setChargerInstructionSteps(chargerModel);
    } else {
      setChargerInstructionSteps(rebateInfo.chargerValidation?.product?.brand!);
    }
  }, [content, chargerModel, rebateInfo]);

  useEffect(() => {
    //Clearing chargerActivatedInAppConfirmed to enable going back and change the decision
    updateFormState('rebateConfirm', {
      ...rebateConfirm,
      chargerActivatedInAppConfirmed: undefined,
    });
  }, []);

  const setChargerInstructionSteps = (model: string) => {
    switch (model) {
      case ChargerType.CHARGE_POINT:
        setInstructionSteps(chargePointInstructions);
        break;

      case ChargerType.FLO:
        setInstructionSteps(floInstructions);
        break;

      case ChargerType.JUICE_BOX:
        setInstructionSteps(juiceBoxInstructions);
        break;

      case ChargerType.EV_PULSE:
        setInstructionSteps(evPulseInstructions);
        break;

      case ChargerType.WALLBOX:
        setInstructionSteps(wallBoxInstructions);
        break;

      case ChargerType.EMPORIA:
        setInstructionSteps(emporiaInstructions);
        break;

      case ChargerType.EVOCHARGE:
        setInstructionSteps(evoChargeInstructions);
        break;
      default:
        setInstructionSteps(chargePointInstructions);
        break;
    }
  };

  const sectionErRebateConfirm: SectionErRebateConfirm = getSectionData(
    ApplicationErSectionType.ErRebateConfirm,
  );

  const { updateSectionRebateConfirm } = useUpdateERSections();

  const { wrapWithLoader } = useWrapWithLoader();
  const { setErrorNotification } = useUtil();
  const onSubmit = wrapWithLoader(async (buttonType: string) => {
    try {
      const response = await updateSectionRebateConfirm(
        rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            chargerActivatedInAppConfirmed:
              buttonType === 'WORKED' ? true : false,
          },
          application?.id!,
          sectionErRebateConfirm?.id,
        ),
      );
      // Save the response in a local var
      const result = response?.data?.updateSectionERRebateConfirm;

      // Check for errors to show the red banner and block navigation
      if (response?.errors || result?.validation?.errors) {
        throw new Error();
      }

      //Update the app state with the user selected values
      if (result?.section.data) {
        const updatedValues = rebateConfirmERFormStateAdapter().toFormState(
          result?.section.data,
        );
        updateFormState('rebateConfirm', {
          ...updatedValues,
        });
      }

      //Redirect to fiddlestick page based on error
      // CUP-24446: Refactor only.. now calls the shared helper (also used by VehicleInstructions.tsx) instead of its own local copy
      if (
        result?.section.errors.length &&
        handleInstructionsSectionErrors(result?.section.errors)
      ) {
        return;
      }
      if (!result?.section.errors.length) {
        if (chargerModel === ChargerType.EV_PULSE) {
          // CUP-24447: EVPulse customers now land on the new vehicle results screen instead of the legacy EVPulse-only results page
          void navigate(routes.PGE_PLUS_REBATE_ONLY_VEHICLE_REBATE_RESULTS);
        } else {
          void navigate(routes.PGE_PLUS_REBATE_ONLY_REBATE_RESULTS);
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateConfirm({
        ...rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            chargerActivatedInAppConfirmed: undefined,
          },
          application?.id!,
          sectionErRebateConfirm?.id,
        ),
        saveAndComeBack: true,
      });
      if (
        response?.errors ||
        response.data?.updateSectionERRebateConfirm?.validation?.errors ||
        response.data?.updateSectionERRebateConfirm?.section?.errors.length !==
          0
      ) {
        throw new Error();
      } else {
        void navigate(routes.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });

  const getBackBtnRoute = () => {
    // CUP-24446: Legacy Tesla resume.. send back to ConnectVehicleToApp (its current equivalent) instead of the retired Connect Tesla screen, so willConnectVehicleManufacturerApp gets answered on the new field
    if (
      isLegacyTeslaResumeCustomer(rebateConfirm) &&
      (!rebateInfo?.chargerValidation?.isQualifiedProduct ||
        rebateInfo?.enrollmentPreference === ErEnrollmentPreference.UseMyVehicle)
    ) {
      return routes.PGE_PLUS_REBATE_ONLY_CONNECT_VEHICLE;
    }
    // Charger flow (from ConnectChargerToWifi): back to connect charger
    return routes.PGE_PLUS_REBATE_ONLY_CONNECT_CHARGER;
  };

  return (
    <Grid
      container
      css={{
        rowGap: 30,
      }}
    >
      <Grid item>
        <Typography
          variant={'h2'}
          css={{
            fontSize: '24px',
            fontWeight: 700,
          }}
        >
          {content.get('CHARGER_INSTRUCTIONS_LETS_CONNECT_TITLE')}
        </Typography>
      </Grid>
      <Grid item>
        <Typography
          variant={'body1'}
          css={{
            fontSize: '20px !important',
            lineHeight: '26px !important',
          }}
        >
          {chargerModel === ChargerType.EV_PULSE ||
          rebateInfo.chargerValidation?.product?.brand === ChargerType.EV_PULSE
            ? content.get('EVPULSE_INSTRUCTIONS_SUB_HEADER')
            : content.get('CHARGER_INSTRUCTIONS_SUB_HEADER')}
        </Typography>
      </Grid>
      <Grid item container>
        <Typography
          variant={'h2'}
          css={{
            fontSize: '20px',
            textAlign: 'center',
          }}
        >
          {content.get('CHARGER_INSTRUCTIONS_HERE_HOW_TEXT')}
        </Typography>
      </Grid>
      <Grid item container>
        {instructionSteps.map(step => {
          return (
            <Grid
              container
              wrap={'nowrap'}
              xs={12}
              css={{
                backgroundColor: colors.white,
                padding: '20px 30px',
                columnGap: 30,
                display: 'flex',
              }}
            >
              <Grid
                item
                css={{
                  display: 'flex',
                  borderRadius: '50%',
                  backgroundColor: colors.noirBlurLight,
                  height: 30,
                  padding: '3px 10px',
                  marginTop: 2,
                }}
              >
                <Typography
                  variant={'h2'}
                  css={{
                    color: colors.white,
                    fontSize: '20px',
                    textAlign: 'center',
                  }}
                >
                  {step.step}
                </Typography>
              </Grid>
              <Grid item container xs={12}>
                <Grid item>
                  <Typography
                    variant={'body1'}
                    css={{
                      fontSize: '20px',
                      '& p:first-child': {
                        margin: 0,
                      },
                      '& p:last-child': {
                        marginBottom: 0,
                      },
                      '& a': {
                        textDecoration: 'none',
                        fontWeight: 700,
                      },
                    }}
                  >
                    {richText(step.text)}
                    {/* <ReactMarkdown source={step.text} escapeHtml={false} /> */}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
      <Grid
        item
        container
        spacing={2}
        css={{
          marginTop: 20,
          justifyContent: 'center',
        }}
      >
        <Grid item xs={6} md={3}>
          <Buttons
            type="button"
            fullWidth
            variant="outlined"
            color="secondary"
            css={{
              fontSize: '18px',
              borderRadius: '30px',
              '.MuiButton-label': {
                fontWeight: 500,
              },
            }}
            onClick={async () => {
              await onSubmit('DIDNT_WORK');
            }}
          >
            {content.get('BUTTON_DIDNT_WORK')}
          </Buttons>
        </Grid>
        <Grid item xs={6} md={3}>
          <Buttons
            type="button"
            fullWidth
            variant="contained"
            css={{
              fontSize: '18px',
              borderRadius: '30px',
              '.MuiButton-label': {
                fontWeight: 500,
              },
            }}
            onClick={async () => {
              await onSubmit('WORKED');
            }}
          >
            {content.get('BUTTON_IT_WORKED')}
          </Buttons>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider css={{ color: colors.gray93, margin: '20px 0 0px 0' }} />
        <ProceedOrCancelButton back={getBackBtnRoute()} next={false} />
      </Grid>
      <Grid item xs={12}>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </Grid>
    </Grid>
  );
};

export default ChargerInstructions;
